import React from 'react';
import { Box, Text, Heading, SimpleGrid, VStack } from '@chakra-ui/react';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface PolicyCardProps {
  sectionData: BinderSectionData;
}

const PolicyCard: React.FC<PolicyCardProps> = ({ sectionData }) => {
  const { data } = sectionData;
  const { policyInfo = [] } = data || {};
  const colors = useBinderCardColors();

  // Handle case when no policy info
  if (policyInfo.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No policy information available
        </Text>
      </Box>
    );
  }

  // Separate fields into different layout groups
  // Row 1: 3-column - Insurance Company, Producer Code, CSR Code
  const threeColumnFields1 = policyInfo.filter(
    (item: any) =>
      item.field === 'Insurance Company' ||
      item.field === 'Producer Code' ||
      item.field === 'CSR Code',
  );

  // Row 2: 4-column - Binder Number, Premium, Customer Code, Broker Code
  const fourColumnFields = policyInfo.filter(
    (item: any) =>
      item.field === 'Binder Number' ||
      item.field === 'Premium' ||
      item.field === 'Customer Code' ||
      item.field === 'Broker Code',
  );

  // Row 3: 2-column - Named Insured & Address, Lessor
  const twoColumnFields1 = policyInfo.filter(
    (item: any) =>
      item.field === 'Named Insured & Address' || item.field === 'Lessor',
  );

  // Row 4: 2-column - Effective Date/Time, Expiry Date/Time
  const twoColumnFields2 = policyInfo.filter(
    (item: any) =>
      item.field === 'Effective Date/Time' || item.field === 'Expiry Date/Time',
  );

  // Row 5: 2-column - Signed Name, Signed Date
  const twoColumnFields3 = policyInfo.filter(
    (item: any) => item.field === 'Signed Name' || item.field === 'Signed Date',
  );

  // Row 6: 3-column - Market Approval Reference, Market Approval Underwriter, Market Approval Date
  const threeColumnFields2 = policyInfo.filter(
    (item: any) =>
      item.field === 'Market Approval Reference' ||
      item.field === 'Market Approval Underwriter' ||
      item.field === 'Market Approval Date',
  );

  const renderField = (item: any) => {
    const isMarketColumn = threeColumnFields2.some(
      (col: any) => col.field === item.field,
    );

    return (
      <VStack align="start" spacing={2}>
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={isMarketColumn ? colors.failedTextColor : colors.labelColor}
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {item.field}
        </Text>
        <Box>
          {item.value === 'N/A' ? (
            <Text color="gray.400" fontStyle="italic" fontSize="sm">
              -
            </Text>
          ) : (
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={colors.textColor}
              wordBreak="break-word"
            >
              {item.value}
            </Text>
          )}
        </Box>
      </VStack>
    );
  };

  return (
    <Box>
      <Box mb={4}>
        <Heading size="sm" color={colors.textColor} mb={2}>
          Policy
        </Heading>
      </Box>

      <Box
        borderRadius="md"
        borderWidth="1px"
        borderColor={colors.borderColor}
        overflow="hidden"
      >
        {/* Row 1: 3-column - Insurance Company, Producer Code, CSR Code */}
        {threeColumnFields1.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={0}>
              {threeColumnFields1.map((item: any, index: number) => (
                <Box
                  key={`three-col-1-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 3 !== 2 ? '1px' : 0,
                    lg: index % 3 !== 2 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 2: 4-column - Binder Number, Premium, Customer Code, Broker Code */}
        {fourColumnFields.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
              {fourColumnFields.map((item: any, index: number) => (
                <Box
                  key={`four-col-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 4 !== 3 ? '1px' : 0,
                    lg: index % 4 !== 3 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 3: 2-column - Named Insured & Address, Lessor */}
        {twoColumnFields1.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
              {twoColumnFields1.map((item: any, index: number) => (
                <Box
                  key={`two-col-1-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index === 0 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 4: 2-column - Effective Date/Time, Expiry Date/Time */}
        {twoColumnFields2.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
              {twoColumnFields2.map((item: any, index: number) => (
                <Box
                  key={`two-col-2-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index === 0 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 5: 2-column - Signed Name, Signed Date */}
        {twoColumnFields3.length > 0 && (
          <Box bg={colors.emptyFieldBgColor}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
              {twoColumnFields3.map((item: any, index: number) => (
                <Box
                  key={`two-col-3-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index === 0 ? '1px' : 0,
                  }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Row 6: 3-column - Market Approval Reference, Market Approval Underwriter, Market Approval Date */}
        {threeColumnFields2.length > 0 && (
          <Box bg="transparent">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={0}>
              {threeColumnFields2.map((item: any, index: number) => (
                <Box
                  key={`three-col-2-${index}`}
                  p={4}
                  borderRightWidth={{
                    base: 0,
                    md: index % 3 !== 2 ? '1px' : 0,
                    lg: index % 3 !== 2 ? '1px' : 0,
                  }}
                  borderBottomWidth={
                    index >= threeColumnFields2.length - 3 ? 0 : '1px'
                  }
                  borderColor={colors.borderColor}
                >
                  {renderField(item)}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PolicyCard;
